import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { User } from '../../modules/security/models/user.model';
import { environment } from '../../../environments/environment';
import { httpHeaders } from '../../../environments/httpHeaders';
import {StorageFile} from '../contents/shared/models/file';
import {BehaviorSubject} from 'rxjs';

const apiURL = environment.apiUrl;
const headers = httpHeaders.headers;

@Injectable({
  providedIn: 'root'
})
export class TreatmentsService {

  private previousResponse: any[];
  private previousRequestParams: any;
  private filterParam: string;
  private deletedFileName = new BehaviorSubject<string>('');

  constructor(
    private _http: HttpClient
  ) { }

  currentFileName = this.deletedFileName.asObservable();

  onDeleteFileName(fileName: string) {
    this.deletedFileName.next(fileName);
  }

  public getDoctors() {
    const endPoint = '/api/admin/byrole/DO';
    return this._http.get(apiURL + endPoint, { headers: headers }).pipe(map((resp: User[]) => {
      return resp;
    }));
  }

  public getTreatments(request) {
    this.previousResponse = [];
    const endPoint = '/api/treatment/search';
    request = this.escapeRequestProp(request);
    return this._http.post(`${apiURL}${endPoint}`, request, { headers: headers }).pipe(map((response: any[]) => {
      this.previousResponse = response;
      this.previousRequestParams = request;
      return response;
    }));
  }

  public updateAssignedUser(idTreatment, idAdmin) {
    this.previousResponse = [];
    const endPoint =  `/api/Treatment/TakeInCharge/${idTreatment}/${idAdmin}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers }).pipe(map((response: any[]) => {
      return response;
    }));
  }

  public updateFaceScanData(data) {
    this.previousResponse = [];
    const endPoint =  `/api/Treatment/UpdateFaceScanData/`;
    return this._http.post(`${apiURL}${endPoint}`, data, { headers: headers }).pipe(map((response: any[]) => {
      return response;
    }));
  }

  public updateOptSmartData(data) {
    this.previousResponse = [];
    const endPoint =  `/api/Treatment/UpdateOptSmartData/`;
    return this._http.post(`${apiURL}${endPoint}`, data, { headers: headers }).pipe(map((response: any[]) => {
      return response;
    }));
  }

  public downloadFaceScanZip(data) {
    this.previousResponse = [];
    const endPoint =  `/api/Storage/zip`;
    return this._http.post<Blob>(`${apiURL}${endPoint}`, data, {observe: 'response', responseType: 'blob' as 'json'});
  }

  public downloadSmartOptionZip(data) {
    this.previousResponse = [];
    const endPoint =  `/api/Storage/zip`;
    return this._http.post<Blob>(`${apiURL}${endPoint}`, data, {observe: 'response', responseType: 'blob' as 'json'});
  }

  public updateSegmentationUser(idTreatment, idAdmin) {
    this.previousResponse = [];
    const endPoint =  `/api/Treatment/SetSegmentationUser/${idTreatment}/${idAdmin}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers }).pipe(map((response: any[]) => {
      return response;
    }));
  }

  public updateExportUser(idTreatment, idAdmin) {
    this.previousResponse = [];
    const endPoint =  `/api/Treatment/SetExportUser/${idTreatment}/${idAdmin}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers }).pipe(map((response: any[]) => {
      return response;
    }));
  }

  public updateCutUser(idTreatment, idAdmin) {
    this.previousResponse = [];
    const endPoint =  `/api/Treatment/SetCutUser/${idTreatment}/${idAdmin}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers }).pipe(map((response: any[]) => {
      return response;
    }));
  }

  public setCutFlag(idTreatment, value) {
    const endPoint =  `/api/Treatment/SetCutFlag/${idTreatment}/${value}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers }).pipe(map((response: any[]) => {
      return response;
    }));
  }

  public setExportFlag(idTreatment) {
    const endPoint =  `/api/Treatment/SetExportFlag/${idTreatment}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers }).pipe(map((response: any[]) => {
      return response;
    }));
  }

  public helpMeOut(body) {
    const endPoint =  `/api/treatment/ChangeStatus/HelpMeOut`;
    return this._http.post(`${apiURL}${endPoint}`, body, { headers: headers }).pipe(map((response: any[]) => {
      return response;
    }));
  }

  public updateCloudType(body) {
    const endPoint =  `/api/Treatment/UpdateCloudType`;
    return this._http.post(`${apiURL}${endPoint}`, body, { headers: headers });
  }

  public getHistory(treatmentId) {
    this.previousResponse = [];
    const endPoint = '/api/treatment/log/' + treatmentId;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers }).pipe(map((response: any[]) => {
      return response;
    }));
  }

  public getTreatment(treatmentId) {
    const endPoint = `/api/treatment/${treatmentId}`;
    return this._http.get(`${apiURL}${endPoint}`, {headers: headers});
  }

  public getTreatmentFiles(treatmentId) {
    const endPoint = `//api/Storage/GetTreatmentFiles/${treatmentId}`;
    return this._http.get(`${apiURL}${endPoint}`, {headers: headers});
  }

  uploadTreatmentFile(file: any, containerName: string) {
    const endPoint = '/api/storage';
    const formData = new FormData();
    formData.append('fileObject', file, file.name);
    formData.append('containerName', containerName);
    formData.append('createContainerIfNotExists', 'true');

    return this._http.put(apiURL + endPoint, formData).pipe(map((resp: StorageFile) => {
      return resp;
    }));
  }

  public getTreatmentsReport(request) {
    const endPoint = '/api/treatment/report';
    return this._http.post(`${apiURL}${endPoint}`, request, { headers: headers }).pipe(map((response: any[]) => {
      return response;
    }));
  }

  public moveTreatmentToBasket(request) {
    const endPoint = '/api/treatment/movetobasket/';
    return this._http.post(`${apiURL}${endPoint}`, request, { headers: headers }).pipe(map((response: any[]) => {
      return response;
    }));
  }

  public existByIdAddress(idAddress) {
    const endPoint = `/api/treatment/existsByIdAddrress/${idAddress}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers });
  }

  escapeRequestProp(request: any): any {
    for (const prop in request) {
      if (typeof (request[prop]) !== 'number') {
        if (!request[prop] && prop !== 'ignoreArchived') {
          delete (request[prop]);
        }
      }
    }
    return request;
  }

  public getPreviousReponse() {
    if (!this.previousResponse) {
      this.previousResponse = [];
    }
    return this.previousResponse;
  }

  public getPreviousRequest() {
    return this.previousRequestParams;
  }

  public reset() {
    this.previousRequestParams = null;
    this.previousResponse = [];
    this.filterParam = '';
  }

  public setFilterParams(val) {
    this.filterParam = val;
  }

  public getFilterParams() {
    return this.filterParam ? this.filterParam : '';
  }

  public changeStatus(reason: string, treatmentId: any, endpoint: string, request?: any) {
    const endPoint = `/api/treatment/changestatus/${endpoint}`;

    request = request ? request : {
      idTreatment: +treatmentId,
      reason: reason
    };

    return this._http.post(`${apiURL}${endPoint}`, request, { headers: headers });
  }

  public removeFromWaiting(treatmentId: any) {
    const endPoint = `/api/treatment/RemoveFromWaitingFor/${treatmentId}`;

    return this._http.get(`${apiURL}${endPoint}`, { headers: headers });
  }

  public closeChat(treatmentId: any) {
    const endPoint = `/api/treatment/chat/closechat/${treatmentId}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers });
  }

  public takeInCharge(idTreatment: any, idAdmin: any) {
    const endPoint = `/api/treatment/takeincharge/${idTreatment}/${idAdmin}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers });
  }

  public closeChatIncomplete(treatmentId: any) {
    const endPoint = `/api/treatment/incomplete/chat/closechat/${treatmentId}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers });
  }

  public setTreatmentPlan(plan: any) {
    const endPoint = `/api/treatmentplan`;
    return this._http.post(`${apiURL}${endPoint}`, plan, { headers: headers });
  }

  public createTreatmentPlanPDF(body: any, idTreatment: string, language: string) {
    const endPoint = `/api/treatmentplan/createpdf/${idTreatment}/${language}`;
    return this._http.post(`${apiURL}${endPoint}`, body, { headers: headers });
  }

  public getTreatmentPlanById(treatmentId: any) {
    const endPoint = `/api/treatmentplan/${treatmentId}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers });
  }

  public goToNext(idTreatment: any, value: boolean) {
    const endPoint = `/api/treatmentplan/gotonext/${idTreatment}/${value}`;

    return this._http.get(`${apiURL}${endPoint}`, { headers: headers });
  }

  public saveAdminData(treatmentId: any, request: any) {
    const endPoint = `/api/Treatmentplan/administrationdata/${treatmentId}`;
    return this._http.post(`${apiURL}${endPoint}`, request, { headers: headers });

  }

  public getScheduleTimes(arg0: string) {
    const endPoint = `/api/Treatment/PickupDates/${arg0}`;

    return this._http.get(`${apiURL}${endPoint}`, { headers: headers });
  }

  putScheduleTime(request: any) {
    const endPoint = `/api/Treatment/PickupDate`;

    return this._http.post(`${apiURL}${endPoint}`, request, { headers: headers });
  }

  public addAdditionalRequest_newPrint(req: any) {
    const endPoint = `/api/TreatmentPlan/AdditionalRequest/NewPrint`;
    return this._http.post(`${apiURL}${endPoint}`, req, { headers: headers });
  }

  public addAdditionalRequest_newRetainers(req: any) {
    const endPoint = `/api/TreatmentPlan/AdditionalRequest/NewRetainers`;
    return this._http.post(`${apiURL}${endPoint}`, req, { headers: headers });
  }

  public addAdditionalRequest_reMake(req: any) {
    const endPoint = `/api/TreatmentPlan/AdditionalRequest/Remake`;
    return this._http.post(`${apiURL}${endPoint}`, req, { headers: headers });
  }

  public addAdditionalRequest_newFreedom(req: any) {
    const endPoint = `/api/TreatmentPlan/AdditionalRequest/NewFreedom`;
    return this._http.post(`${apiURL}${endPoint}`, req, { headers: headers });
  }

  public addAdditionalRequest_newIntercept(req: any) {
    const endPoint = `/api/TreatmentPlan/AdditionalRequest/NewIntercept`;
    return this._http.post(`${apiURL}${endPoint}`, req, { headers: headers });
  }

  public addAdditionalRequest_updateStatus(req: any) {
    const endPoint = `/api/TreatmentPlan/AdditionalRequest`;
    return this._http.put(`${apiURL}${endPoint}`, req, { headers: headers });
  }

  public addAdditionalRequest_delete(idrequest: any) {
    const endPoint = `/api/TreatmentPlan/AdditionalRequest`;
    return this._http.delete(`${apiURL}${endPoint}/` + idrequest, { headers: headers });
  }

  public updateWebViewerFile(data: any) {
    const endPoint = `/api/treatment/updatewebviewerfile`;
    return this._http.post(`${apiURL}${endPoint}`, data, { headers: headers });
  }

  public deleteWebViewer(treatmentId: any) {
    const endPoint = `/api/treatment/deletewebviewer/${treatmentId}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers });
  }

  public updateAddresses(data: any) {
    const endPoint = `/api/treatment/updateaddresses`;
    return this._http.post(`${apiURL}${endPoint}`, data, { headers: headers });
  }

  public changeDoctor(data: any) {
    const endPoint = `/api/treatment/movedoctor`;
    return this._http.post(`${apiURL}${endPoint}`, data, { headers: headers });
  }

  public cloneTreatment(data: any) {
    const endPoint = `/api/treatment/clone`;
    return this._http.post(`${apiURL}${endPoint}`, data, { headers: headers });
  }

  public cloneTellMeYes(data: any) {
    const endPoint = `/api/TellMeYes/clone`;
    return this._http.post(`${apiURL}${endPoint}`, data, { headers: headers });
  }

  public getDoctorHistory(idTreatment: any) {
    const endPoint = `/api/Treatment/MoveDoctor/Log/${idTreatment}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers });
  }

  public generateTreatmentCode(treatmentId: any, archesType: string) {
    const endPoint = `/api/treatment/generatetreatmentcode/${treatmentId}/${archesType}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers });
  }

  public updatePatientAdditionalNotes(idTreatment: any, treatmentCode: string, notes: string) {
    const endPoint = `/api/treatment/updatepatientnotes`;
    const data = {
      'idtreatment': +idTreatment,
      'treatmentCode': treatmentCode,
      'patientAdditionalNotes': notes
    };
    return this._http.post(`${apiURL}${endPoint}`, data, { headers: headers });
  }

  public updateTreatmentPlanNotes(body: any) {
    const endPoint = `/api/treatmentplan/UpdateTreatmentPlanNotes`;
    return this._http.post(`${apiURL}${endPoint}`, body, { headers: headers });
  }

  public updateClinicPreferenceFlag(idDoctor: any) {
    const endPoint = `/api/admin/doctor/clinicprefflag/${idDoctor}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers });
  }

  public updatePlannedShippingDate(body: any) {
    const endPoint = `/api/treatment/updateplannedshippingdate`;
    return this._http.post(`${apiURL}${endPoint}`, body, { headers: headers });
  }

  public removePlannedShippingDate(body: any) {
    const endPoint = `/api/treatment/DeletePlannedShippingDate/`;
    return this._http.get(`${apiURL}${endPoint}${body.idTreatment}`, { headers: headers });
  }

  public sendChatNotification(idTreatment: any) {
    const endPoint = `/api/treatment/sendchatnotification/${idTreatment}`;
    return this._http.get(`${apiURL}${endPoint}`, { headers: headers });
  }

}


